// @ts-strict-ignore
/** @jsx jsx */
import { jsx, Flex, Box } from 'theme-ui'
import { useState, useEffect, useCallback, useRef } from 'react'
import InputGroup from '../../elements/InputGroup/input-group'
import { RangeSliderStyles, localizedInputs } from './styles'
import {
  calculateTotalInterestAndPeriod,
  calculateMinimumRepayment,
} from '../../../../helpers/mortgages-calculations'
import { CURRENCY_SYMBOL } from '../../constants'
import FormatCurrency from '../../utils/currency-formatter'

const debounce = (fn, delay) => {
  let timeout
  return (...args) => {
    window.clearTimeout(timeout)
    timeout = window.setTimeout(() => fn(...args), delay)
  }
}

const CreditCardCalculator = (): JSX.Element => {
  const [errorMessage, setErrorMessage] = useState<string>('')

  const [calculations, setCalculations] = useState({
    interes: '0',
    total: '0',
    period: '0',
    error: '',
  })

  const sliderRef = useRef<HTMLInputElement | null>(null)

  const [data, setData] = useState({
    balance: '3500',
    apr: '18.9',
    amount: '95',
    errorMessage: '',
  })

  const returnData = (key: string, returnValue: string) => {
    setData((prevData) => ({
      ...prevData,
      [key]: returnValue,
    }))
  }

  const mortgageInputChange = (e: string, value: string) => returnData(e, value)

  const handleSliderChange = (e: any) => {
    const { name, value } = e.target
    mortgageInputChange(name, value)
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debounceSlider = useCallback(
    debounce((param: any) => {
      const result = calculateTotalInterestAndPeriod(
        parseInt(param.balance),
        parseFloat(param.apr),
        parseInt(param.amount),
      )

      if (result.error) {
        const errorFun = calculateMinimumRepayment(
          parseInt(param.balance),
          parseFloat(param.apr),
          1,
          5,
        )

        setErrorMessage(`Please ensure monthly payments are more than £${errorFun}.`)
        if (sliderRef?.current) {
          sliderRef.current.max = String(parseInt(errorFun + 4))
        }
        return
      }

      setCalculations(result)
      setErrorMessage('')
    }, 400),
    [],
  )

  useEffect(() => {
    if (!data) return
    // here add calculation call and debounce it
    debounceSlider(data)
  }, [data, debounceSlider])

  return (
    <div
      sx={{
        paddingTop: '2.5em',
        paddingBottom: '2.5em',
        backgroundColor: '#e2e2e2',
      }}
    >
      <div
        sx={{
          background: '#fff',
          padding: '32px',
        }}
      >
        <Flex sx={{ flexWrap: 'wrap' }}>
          <Box
            sx={{
              flex: ['0 0 100%', '0 0 100%', '0 0 33.33333%'],
              maxWidth: ['100%', '100%', '33.33333%'],
              padding: '0 15px',
            }}
          >
            <h1 sx={headingStyle}>Credit Card Interest Calculator</h1>
            <p sx={textStyles}>
              Our credit card interest calculator shows you the total cost of your credit card and
              how long it will take to pay off.
            </p>
          </Box>
          <Box
            sx={{
              flex: ['0 0 100%', '0 0 50%', '0 0 33.33333%'],
              maxWidth: ['100%', '50%', '33.33333%'],
              padding: '0 15px',
            }}
          >
            <div
              // @ts-ignore
              css={localizedInputs}
              sx={{ padding: '10px' }}
            >
              <InputGroup
                labelText={'Card balance:'}
                prefix={'£'}
                inputName={'balance'}
                bindValue={data.balance}
                minValue={0}
                returnInputValue={mortgageInputChange}
              />
            </div>
            <div
              // @ts-ignore
              css={localizedInputs}
              sx={{ padding: '10px' }}
            >
              <InputGroup
                labelText={'Card APR:'}
                inputName={'apr'}
                suffix={'%'}
                bindValue={data.apr}
                minValue={0}
                maxValue={40}
                returnInputValue={mortgageInputChange}
              />
            </div>
            <div
              // @ts-ignore
              css={localizedInputs}
              sx={{ padding: '10px', background: '#f4f5f6' }}
            >
              <InputGroup
                labelText={'Monthly repayment:'}
                prefix={'£'}
                inputName={'amount'}
                bindValue={data.amount}
                minValue={0}
                returnInputValue={mortgageInputChange}
                responseError={errorMessage}
              />
              <div
                sx={{
                  fontSize: '.9em',
                  color: '#65717b',
                  clear: 'both',
                  paddingTop: '5px',
                }}
              >
                slide to adjust the amount
              </div>
              <input
                // @ts-ignore
                css={RangeSliderStyles}
                name='amount'
                type='range'
                ref={sliderRef}
                onChange={handleSliderChange}
                value={data.amount}
                step='5'
                min='86'
                max='1000'
              />
            </div>
            <Flex sx={{ padding: '15px' }}>
              <div
                sx={{
                  width: '50%',
                  float: 'left',
                  textAlign: 'center',
                  padding: '0 10px',
                }}
              >
                <strong sx={{ fontSize: '1.5em' }}>
                  £
                  {calculations && calculations.total && Number(calculations.total) !== 0
                    ? FormatCurrency(calculations.total, 'GBP', CURRENCY_SYMBOL)
                    : '0'}
                </strong>
                <div>total repayable incl. interest</div>
              </div>
              <div
                sx={{
                  width: '50%',
                  float: 'left',
                  textAlign: 'center',
                  padding: '0 10px',
                }}
              >
                <strong sx={{ fontSize: '1.5em' }}>{calculations.period}</strong>
                <span> months</span>
                <div>until card balance paid off</div>
              </div>
            </Flex>
          </Box>
          <Box
            sx={{
              flex: ['0 0 100%', '0 0 50%', '0 0 33.33333%'],
              maxWidth: ['100%', '50%', '33.33333%'],
              padding: '0 15px',
            }}
          >
            <p sx={textStylesSmall}>
              To get started enter the balance and APR of your credit card, then adjust your
              repayments. The more you pay a month the quicker you can pay off your card and the
              less you&apos;ll pay overall.
            </p>
            <p sx={textStylesSmall}>
              Can&apos;t afford to pay more? Transfering your debt onto a 0% balance transfer card
              could save you money:
            </p>
            <a
              sx={buttonStyles}
              href='https://www.uswitch.com/credit-cards/credit-card-balance-transfers/?ref=repayment-calculator'
            >
              See balance transfer cards
            </a>
          </Box>
        </Flex>
      </div>
    </div>
  )
}

const headingStyle = {
  marginTop: '0',
  marginBottom: '0',
  paddingBottom: '.5em',
  lineHeight: '1.3em',
  fontSize: ['1.25em', '2.25em', '2.25em'],
  fontWeight: 'bold',
  color: '#141414',
}

const textStyles = {
  display: ['none', 'block', 'block'],
  fontSize: '18px',
  lineHeight: '1.618',
  padding: '5px 0 0',
  margin: '0 0 1.5em',
  color: '#141414',
}
const textStylesSmall = {
  fontSize: '16px',
  margin: '0 0 1.5em',
}

const buttonStyles = {
  padding: ['.63em 1.25em', '.63em 1.25em', '1em 1.25em'],
  fontWeight: '400',
  color: '#fff !important',
  backgroundColor: '#141414',
  borderColor: '#10101d',
  textDecoration: 'none',
  display: 'block',
  width: '100%',
  border: '0',
  borderRadius: '0',
  fontSize: '1em',
  lineHeight: '1em',
  textAlign: 'center' as const,
  whiteSpace: 'nowrap' as const,
  verticalAlign: 'middle',
  cursor: 'pointer',
  transition: 'none',
}
export default CreditCardCalculator
